import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  DialogActions,
} from "@mui/material";

interface BulkActionFailure {
  executionId: string;
  memberName?: string;
  memberEmail?: string;
  error: string;
}

interface BulkActionFailureDialogProps {
  open: boolean;
  onClose: () => void;
  actionType: "approve" | "execute" | "delete";
  failures: BulkActionFailure[];
  successCount: number;
  totalCount: number;
}

const BulkActionFailureDialog: React.FC<BulkActionFailureDialogProps> = ({
  open,
  onClose,
  actionType,
  failures,
  successCount,
  totalCount,
}) => {
  const getActionText = () => {
    switch (actionType) {
      case "approve":
        return "approve";
      case "execute":
        return "execute";
      case "delete":
        return "delete";
      default:
        return "process";
    }
  };

  const getTitle = () => {
    const actionText = getActionText();
    const failureCount = failures.length;
    
    if (successCount === 0) {
      return `Failed to ${actionText} all ${totalCount} execution${totalCount === 1 ? "" : "s"}`;
    } else if (failureCount > 0) {
      return `Partially completed: ${successCount}/${totalCount} executions ${actionText}d successfully`;
    }
    
    return `Failed to ${actionText} executions`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" color="error">
            {getTitle()}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {successCount > 0 && (
              <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                ✓ {successCount} execution{successCount === 1 ? "" : "s"} {getActionText()}d successfully
              </Typography>
            )}
            <Typography variant="body2" color="error.main">
              ✗ {failures.length} execution{failures.length === 1 ? "" : "s"} failed
            </Typography>
          </Typography>
        </Box>

        {failures.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Failed Executions:
            </Typography>
            <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
              {failures.map((failure, index) => (
                <ListItem key={failure.executionId} sx={{ pl: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {failure.memberName || "Unknown Member"}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {failure.memberEmail && (
                          <Typography variant="caption" color="text.secondary">
                            {failure.memberEmail}
                          </Typography>
                        )}
                        <Typography variant="caption" color="error.main" display="block">
                          Error: {failure.error}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkActionFailureDialog;